import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, ChevronDown, Menu, X, Bookmark } from "lucide-react";
import { getCases, MARKETS } from "@/lib/apiClient";
import { useSite } from "@/context/SiteContext";

const NAV = [
  { label: "Discover", to: "/" },
  { label: "Patterns", to: "/patterns" },
  { label: "Trends", to: "/trends" },
  { label: "Saved", to: "/saved" },
  { label: "About", to: "/about" },
];

function SearchBox({ onNavigate }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const boxRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); setOpen(false); return; }
    const t = setTimeout(async () => {
      const data = await getCases({ q: q.trim(), limit: 6 });
      setResults(data);
      setOpen(true);
    }, 180);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const go = (slug) => { setOpen(false); setQ(""); onNavigate?.(); navigate(`/case/${slug}`); };

  const submit = (e) => {
    e.preventDefault();
    if (results[0]) go(results[0].slug);
  };

  return (
    <div className="relative w-full" ref={boxRef}>
      <form onSubmit={submit}>
        <div className={`flex items-center gap-2 border-2 bg-white px-3 h-11 transition-all ${focused ? "border-ink shadow-[3px_3px_0_0_#0A0A0C]" : "border-ink/20"}`}>
          <Search size={17} className="text-ink/50 shrink-0" />
          <input
            data-testid="header-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onFocus={() => { setFocused(true); if (results.length) setOpen(true); }}
            onBlur={() => setFocused(false)}
            placeholder="Search companies, industries, transformations…"
            className="w-full bg-transparent outline-none text-sm placeholder:text-ink/40"
          />
        </div>
      </form>
      {open && (
        <div className="absolute z-50 mt-2 w-full bg-white border-2 border-ink shadow-[5px_5px_0_0_#0A0A0C] enter" data-testid="search-results-panel">
          {results.length === 0 ? (
            <div className="px-4 py-4 text-sm text-ink/50">No matching transformations.</div>
          ) : (
            results.map((c) => (
              <button
                key={c.slug}
                data-testid={`search-result-${c.slug}`}
                onMouseDown={() => go(c.slug)}
                className="w-full text-left px-4 py-3 border-b border-ink/10 last:border-0 hover:bg-bryellow/40 transition-colors"
              >
                <div className="eyebrow text-ink/50">{c.industry} · {c.country}</div>
                <div className="font-display font-bold text-sm leading-tight">{c.company} — {c.headline}</div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function MarketSelector() {
  const { market, setMarket } = useSite();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="market-country-selector"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 h-11 px-3 border-2 border-ink bg-white text-sm font-semibold btn-tactile whitespace-nowrap"
      >
        <span className="text-base leading-none">{market.flag}</span>
        <span className="hidden sm:inline">{market.name}</span>
        <ChevronDown size={15} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-2 w-52 bg-white border-2 border-ink shadow-[5px_5px_0_0_#0A0A0C] enter">
          {MARKETS.map((m) => (
            <button
              key={m.code}
              data-testid={`market-option-${m.code}`}
              onClick={() => { setMarket(m); setOpen(false); }}
              className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left hover:bg-bryellow/40 transition-colors ${m.code === market.code ? "font-bold" : ""}`}
            >
              <span className="text-base">{m.flag}</span> {m.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { saved } = useSite();

  return (
    <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b-2 border-ink">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-4 py-3">
          <Link to="/" className="shrink-0 group" data-testid="masthead-title" aria-label="AI YAI YAI — Look what AI is doing to business">
            <img src="/logo.webp" alt="AI YAI YAI! — Look what AI is doing to business" className="h-11 sm:h-14 w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center gap-7 mx-4">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-link-${n.label.toLowerCase()}`}
                className={({ isActive }) => `nav-underline font-semibold text-sm ${isActive ? "active" : "text-ink/80 hover:text-ink"}`}
              >
                {n.label === "Saved" && saved.length > 0 ? `Saved (${saved.length})` : n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3 flex-1 max-w-md">
            <SearchBox />
          </div>
          <div className="hidden lg:block">
            <MarketSelector />
          </div>

          <div className="flex lg:hidden items-center gap-2">
            <MarketSelector />
            <button data-testid="mobile-menu-toggle" onClick={() => setMobileOpen((o) => !o)} className="h-11 w-11 flex items-center justify-center border-2 border-ink btn-tactile">
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        <div className="lg:hidden pb-3">
          <SearchBox onNavigate={() => setMobileOpen(false)} />
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t-2 border-ink bg-white enter">
          <nav className="flex flex-col px-4 py-2">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                onClick={() => setMobileOpen(false)}
                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                className={({ isActive }) => `py-3 border-b border-ink/10 font-display font-bold text-lg flex items-center gap-2 ${isActive ? "text-brorange" : ""}`}
              >
                {n.label === "Saved" && <Bookmark size={16} />} {n.label}
                {n.label === "Saved" && saved.length > 0 ? ` (${saved.length})` : ""}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
